/* eslint-disable react/no-unescaped-entities */
/**
 * AdminLeadsQualifPanel — Dashboard admin des leads du questionnaire "closer".
 * Affiche la liste triée par score décroissant, permet de changer le statut,
 * ajouter une note et voir le détail complet.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, Sparkles, Users, Target, Filter, X, ChevronDown, MessageSquare, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OBJECTIF_LBL = { perte_gras: 'Perte gras', prise_muscle: 'Muscle', douleurs_dos: 'Dos', reprise: 'Reprise', autre: 'Autre' };
const BUDGET_LBL = { moins_150: '< 150', '150_300': '150-300', '300_600': '300-600', plus_600: '> 600' };
const TIMING_LBL = { cette_semaine: 'Cette sem.', ce_mois: 'Ce mois', '2_3_mois': '2-3 mois', pas_presse: 'Pas pressé' };
const NUTRITION_LBL = { non: 'Non', compromis: 'Compromis', oui_100: 'Oui 100%' };
const SEANCES_LBL = { '0_1': '0-1x', '2': '2x', '3_4': '3-4x', '5_plus': '5+' };
const PARCOURS_LBL = { online: 'Online', hybride: 'Hybride' };
const STATUS_LBL = {
  new: '🆕 Nouveau', contacted: '📞 Contacté', booked: '📅 Réservé',
  closed_won: '✅ Gagné', closed_lost: '❌ Perdu', blacklist: '🚫 Blacklist',
};

export const AdminLeadsQualifPanel = ({ csrfToken, fmtDate }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | qualified | unqualified | online | hybride
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/api/admin/qualif-leads`, { credentials: 'include' }),
        fetch(`${API}/api/admin/qualif-leads/stats`, { credentials: 'include' }),
      ]);
      if (r1.ok) {
        const data = await r1.json();
        setLeads(data.leads || []);
      }
      if (r2.ok) setStats(await r2.json());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = leads;
    if (filter === 'qualified') arr = arr.filter((l) => l.qualified);
    if (filter === 'unqualified') arr = arr.filter((l) => !l.qualified);
    if (filter === 'online') arr = arr.filter((l) => l.parcours_type === 'online');
    if (filter === 'hybride') arr = arr.filter((l) => l.parcours_type === 'hybride');
    return arr;
  }, [leads, filter]);

  const changeStatus = async (leadId, status) => {
    try {
      const res = await fetch(`${API}/api/admin/qualif-leads/${leadId}/status?status=${status}`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        credentials: 'include',
      });
      if (res.ok) {
        setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
        if (selected && selected.id === leadId) setSelected({ ...selected, status });
      }
    } catch (e) { setError(String(e)); }
  };

  const saveNote = async (leadId, note) => {
    try {
      const res = await fetch(`${API}/api/admin/qualif-leads/${leadId}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: JSON.stringify({ note }),
      });
      if (res.ok) {
        setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, note } : l)));
        if (selected && selected.id === leadId) setSelected({ ...selected, note });
      }
    } catch (e) { setError(String(e)); }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Supprimer définitivement cette candidature ? Cette action est irréversible.')) return;
    try {
      const res = await fetch(`${API}/api/admin/qualif-leads/${leadId}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken },
        credentials: 'include',
      });
      if (res.ok) {
        setLeads((ls) => ls.filter((l) => l.id !== leadId));
        if (selected && selected.id === leadId) setSelected(null);
      }
    } catch (e) { setError(String(e)); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats top */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatBox icon={Users} label="Candidatures totales" value={stats.total} color="#E91E63" />
          <StatBox icon={Sparkles} label="Qualifiés" value={stats.qualified} color="#22c55e" />
          <StatBox icon={Target} label="Taux qualif." value={`${stats.qualification_rate}%`} color="#8B5CF6" />
          <StatBox icon={Users} label="Online / Hybride" value={`${stats.by_parcours?.online || 0} / ${stats.by_parcours?.hybride || 0}`} color="#F59E0B" />
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <Filter className="w-4 h-4 text-zinc-500" />
        {[
          { k: 'all', l: `Tous (${leads.length})` },
          { k: 'qualified', l: `Qualifiés (${leads.filter(l => l.qualified).length})` },
          { k: 'unqualified', l: `Non qualifiés (${leads.filter(l => !l.qualified).length})` },
          { k: 'online', l: 'Online' },
          { k: 'hybride', l: 'Hybride' },
        ].map((f) => (
          <button
            key={f.k}
            type="button"
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === f.k ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
            data-testid={`leads-filter-${f.k}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>}

      {/* Table */}
      <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-leads-qualif-table">
          <thead className="bg-zinc-800/70 text-zinc-400">
            <tr>
              {['Date', 'Score', 'Nom', 'Contact', 'Parcours', 'Budget', 'Séances', 'Timing', 'Statut', '', ''].map((h, idx) => (
                <th key={`${h}-${idx}`} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-zinc-500">Aucune candidature</td></tr>
            )}
            {filtered.map((l) => (
              <tr key={l.id} className="border-t border-white/5 text-zinc-300 hover:bg-white/5">
                <td className="px-4 py-3 whitespace-nowrap text-zinc-500 text-xs">{fmtDate(l.created_at)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-md font-bold text-xs ${l.qualified ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                    {l.score}/100
                  </span>
                </td>
                <td className="px-4 py-3 text-white font-medium whitespace-nowrap">{l.first_name} {l.last_name}</td>
                <td className="px-4 py-3 text-xs">
                  <div>{l.email}</div>
                  <div className="text-zinc-500">{l.phone}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap"><span className="text-xs bg-white/5 px-2 py-0.5 rounded">{PARCOURS_LBL[l.parcours_type] || l.parcours_type}</span></td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">{BUDGET_LBL[l.budget] || l.budget}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">{SEANCES_LBL[l.seances] || l.seances}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">{TIMING_LBL[l.timing] || l.timing}</td>
                <td className="px-4 py-3">
                  <StatusSelect value={l.status} onChange={(v) => changeStatus(l.id, v)} testId={`lead-status-${l.id}`} />
                </td>
                <td className="px-4 py-3">
                  <button type="button" onClick={() => setSelected(l)} className="text-xs text-primary hover:underline" data-testid={`lead-detail-${l.id}`}>
                    Détail →
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button type="button" onClick={() => deleteLead(l.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-qualif-lead-${l.id}`} title="Supprimer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <LeadDetailModal
          lead={selected}
          onClose={() => setSelected(null)}
          onSaveNote={(note) => saveNote(selected.id, note)}
        />
      )}
    </div>
  );
};

const StatBox = ({ icon: Icon, label, value, color }) => (
  <div className="p-4 rounded-xl bg-zinc-900 border border-white/10">
    <div className="flex items-center gap-2 text-xs text-zinc-400 mb-1"><Icon className="w-3.5 h-3.5" style={{ color }} /> {label}</div>
    <div className="text-2xl font-black text-white">{value}</div>
  </div>
);

const StatusSelect = ({ value, onChange, testId }) => (
  <div className="relative">
    <select
      value={value || 'new'}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="appearance-none bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 pr-6 text-xs text-white cursor-pointer hover:bg-zinc-700"
    >
      {Object.entries(STATUS_LBL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
    <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500 pointer-events-none" />
  </div>
);

const LeadDetailModal = ({ lead, onClose, onSaveNote }) => {
  const [note, setNote] = useState(lead.note || '');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur" onClick={onClose}>
      <div className="bg-zinc-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className={`p-5 ${lead.qualified ? 'bg-gradient-to-r from-emerald-500/20 to-green-400/10' : 'bg-gradient-to-r from-amber-500/10 to-zinc-800'} border-b border-white/10 flex items-start justify-between`}>
          <div>
            <div className="text-xs font-bold tracking-widest opacity-70">BODY-UP · CANDIDATURE {PARCOURS_LBL[lead.parcours_type]?.toUpperCase()}</div>
            <div className="text-2xl font-black text-white mt-1">{lead.first_name} {lead.last_name}</div>
            <div className="text-3xl font-black mt-1">
              <span className={lead.qualified ? 'text-emerald-400' : 'text-amber-400'}>{lead.score}/100</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="lead-detail-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Email" value={<a href={`mailto:${lead.email}`} className="text-primary hover:underline">{lead.email}</a>} />
            <Info label="Téléphone" value={<a href={`tel:${lead.phone}`} className="text-primary hover:underline">{lead.phone}</a>} />
            <Info label="Ville" value={lead.city || '—'} />
            <Info label="Motivation" value={`${lead.motivation}/10`} />
            <Info label="Objectif" value={OBJECTIF_LBL[lead.objectif] || lead.objectif} />
            <Info label="💰 Budget/mois" value={<strong className="text-primary">{BUDGET_LBL[lead.budget]}</strong>} />
            <Info label="🏋️ Séances/sem" value={SEANCES_LBL[lead.seances]} />
            <Info label="🥗 Nutrition" value={NUTRITION_LBL[lead.nutrition]} />
            <Info label="⏰ Timing" value={TIMING_LBL[lead.timing]} />
            <Info label="✅ Engagement" value={lead.engagement_rdv ? 'Oui' : 'Non'} />
          </div>
          {lead.obstacle && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <div className="text-xs font-bold text-amber-300 mb-1 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Ce qui l'empêche</div>
              <div className="text-zinc-200 italic">« {lead.obstacle} »</div>
            </div>
          )}
          <div>
            <label className="text-xs font-bold text-zinc-400 tracking-wider uppercase mb-1.5 block">Note interne</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-white/10 focus:border-primary focus:outline-none text-white text-sm"
              placeholder="Notes pour préparer le call, objections identifiées, etc."
              data-testid="lead-note-textarea"
            />
            <button type="button" onClick={() => onSaveNote(note)} className="mt-2 px-4 py-2 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary/90" data-testid="lead-note-save">
              Enregistrer la note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-bold text-zinc-500 tracking-wider uppercase">{label}</div>
    <div className="text-white mt-0.5">{value}</div>
  </div>
);
